import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button, Select, SelectItem, Modal, ModalContent, ModalHeader, ModalBody, useDisclosure, Input, Chip, Badge, Tabs, Tab } from "@heroui/react";
import { Plus, X, Search } from "lucide-react";
import Session from "./Session";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAllTagsAsync } from "../redux/tag";
import { getAllTranscriptionTagsAsync } from "../redux/transcriptionTag";
import { getAllTranscriptionsAsync } from "../redux/transcription";
import clsx from 'clsx';
import SitzungViewConfig from "../components/session/SitzungViewConfig";
import {
  loadSelectedItemsForCurrentUser,
  saveSelectedItemsForCurrentUser,
} from "../services/userScopedStorage";

// Global store for multiscreen tabs
if (!window.multiscreenTabsStore) {
  window.multiscreenTabsStore = {
    tabs: [
      { id: 1, sessionId: null, label: "Tab 1" },
      { id: 'settings', label: "Summarizer", isConfig: true, sessionId: null, configData: { localNavData: [], speakers: [], paragraphChildren: [] } }
    ],
    activeTab: "1",
    nextTabId: 2,
    storeVersion: 0
  };
}

const Multiscreen = () => {
  const navigate = useNavigate();
  const { sessionId: sessionIdFromUrl } = useParams();
  const { transcriptions } = useSelector((state) => state.transcriptions);
  const dispatch = useDispatch();
  
  // Helper function to ensure config tabs are always last
  const sortTabsWithConfigLast = (tabsArray) => {
    const sessionTabs = tabsArray.filter(t => !t.isConfig);
    const configTabs = tabsArray.filter(t => t.isConfig);
    return [...sessionTabs, ...configTabs];
  };
  
  // Initialize from global store
  const [tabs, setTabs] = useState(() => sortTabsWithConfigLast(window.multiscreenTabsStore.tabs));
  const [activeTab, setActiveTab] = useState(() => window.multiscreenTabsStore.activeTab);
  const [nextTabId, setNextTabId] = useState(() => window.multiscreenTabsStore.nextTabId);
  const [storeVersion, setStoreVersion] = useState(0); // Track store updates for badges
  const { isOpen: isSelectorOpen, onOpen: onSelectorOpen, onClose: onSelectorClose } = useDisclosure();
  const nextTabIdRef = useRef(2);
  
  // Use refs to track if we're updating from internal changes
  const isInternalUpdateRef = useRef(false);
  
  // Sync state to global store
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    
    const sortedTabs = sortTabsWithConfigLast(tabs);
    const prevTabs = window.multiscreenTabsStore.tabs;
    const prevActiveTab = window.multiscreenTabsStore.activeTab;
    const prevNextTabId = window.multiscreenTabsStore.nextTabId;
    
    // Only update if something actually changed
    const tabsChanged = JSON.stringify(sortedTabs) !== JSON.stringify(prevTabs);
    const activeTabChanged = activeTab !== prevActiveTab;
    const nextTabIdChanged = nextTabId !== prevNextTabId;
    
    if (tabsChanged || activeTabChanged || nextTabIdChanged) {
      window.multiscreenTabsStore.tabs = sortedTabs;
      window.multiscreenTabsStore.activeTab = activeTab;
      window.multiscreenTabsStore.nextTabId = nextTabId;
      window.multiscreenTabsStore.storeVersion = storeVersion;
      
      // Dispatch event with source marker
      window.dispatchEvent(new CustomEvent('multiscreen-tabs-updated', {
        detail: { tabs: sortedTabs, activeTab, nextTabId, storeVersion, source: 'multiscreen' }
      }));
    }
  }, [tabs, activeTab, nextTabId, storeVersion]);
  
  // Listen for external updates to tabs (only for external updates, not our own)
  useEffect(() => {
    const handleTabsUpdate = (event) => {
      // Only update if the event didn't come from this component
      if (event.detail && event.detail.source !== 'multiscreen') {
        isInternalUpdateRef.current = true;
        
        if (event.detail.tabs) {
          setTabs(prevTabs => {
            const sorted = sortTabsWithConfigLast(event.detail.tabs);
            // Only update if actually different
            if (JSON.stringify(sorted) !== JSON.stringify(prevTabs)) {
              return sorted;
            }
            return prevTabs;
          });
        }
        if (event.detail.activeTab) {
          setActiveTab(prev => event.detail.activeTab !== prev ? event.detail.activeTab : prev);
        }
        if (event.detail.nextTabId) {
          setNextTabId(prev => event.detail.nextTabId !== prev ? event.detail.nextTabId : prev);
        }
      }
    };
    window.addEventListener('multiscreen-tabs-updated', handleTabsUpdate);
    return () => window.removeEventListener('multiscreen-tabs-updated', handleTabsUpdate);
  }, []); // Empty deps - listener doesn't need to change
  
  // Keep ref in sync with state
  useEffect(() => {
    nextTabIdRef.current = nextTabId;
  }, [nextTabId]);
  
  // Listen for store updates to refresh badges - debounced to prevent excessive re-renders
  useEffect(() => {
    let timeoutId = null;
    const handleStoreUpdate = () => {
      // Debounce updates to prevent excessive re-renders
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setStoreVersion(prev => prev + 1);
      }, 100); // 100ms debounce
    };
    
    window.addEventListener('selected-items-store-updated', handleStoreUpdate);
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('selected-items-store-updated', handleStoreUpdate);
    };
  }, []);
  
  // Listen for config tab addition events
  useEffect(() => {
    const handleAddConfigTab = (event) => {
      const { sessionId, sessionName, localNavData, speakers, paragraphChildren } = event.detail;
      
      setTabs((prevTabs) => {
        // Check if Settings tab already exists
        const settingsTabIndex = prevTabs.findIndex(tab => tab.isConfig);
        
        if (settingsTabIndex !== -1) {
          // Update existing Settings tab
          const updatedTabs = [...prevTabs];
          updatedTabs[settingsTabIndex] = {
            ...updatedTabs[settingsTabIndex],
            sessionId: String(sessionId),
            configData: {
              localNavData: Array.isArray(localNavData) ? localNavData : [],
              speakers: Array.isArray(speakers) ? speakers : [],
              paragraphChildren: Array.isArray(paragraphChildren) ? paragraphChildren : [],
            }
          };
          
          // Switch to Settings tab
          setActiveTab(String(updatedTabs[settingsTabIndex].id));
          
          return sortTabsWithConfigLast(updatedTabs);
        }
        
        // If no Settings tab exists (shouldn't happen now), create one
        if (prevTabs.length >= 5) return prevTabs;
        
        const newTabId = nextTabIdRef.current;
        const newTab = {
          id: newTabId,
          sessionId: String(sessionId),
          label: "Summarizer",
          isConfig: true,
          configData: {
            localNavData: Array.isArray(localNavData) ? localNavData : [],
            speakers: Array.isArray(speakers) ? speakers : [],
            paragraphChildren: Array.isArray(paragraphChildren) ? paragraphChildren : [],
          }
        };
        
        setNextTabId(newTabId + 1);
        setActiveTab(String(newTabId));
        
        return sortTabsWithConfigLast([...prevTabs, newTab]);
      });
    };

    const handleSwitchToSessionTab = (event) => {
      const { sessionId } = event.detail;
      
      // Find and switch to session tab
      setTabs((prevTabs) => {
        const sessionTab = prevTabs.find(tab => 
          tab.sessionId === String(sessionId) && !tab.isConfig
        );
        
        if (sessionTab) {
          // Schedule active tab update to avoid nested state update
          requestAnimationFrame(() => {
            setActiveTab(String(sessionTab.id));
          });
        }
        
        // Return unchanged tabs
        return prevTabs;
      });
    };

    window.addEventListener('add-config-tab', handleAddConfigTab);
    window.addEventListener('switch-to-session-tab', handleSwitchToSessionTab);
    return () => {
      window.removeEventListener('add-config-tab', handleAddConfigTab);
      window.removeEventListener('switch-to-session-tab', handleSwitchToSessionTab);
    };
  }, []);
  
  // Initialize or update first tab - only use URL sessionId, otherwise show selector
  useEffect(() => {
    if (!transcriptions || transcriptions.length === 0) return;
    
    // Only use sessionId from URL if provided
    if (sessionIdFromUrl) {
      const targetSession = (transcriptions || []).find(s => String(s.id) === String(sessionIdFromUrl));
      if (targetSession) {
        const sessionName = targetSession.name || `Session ${sessionIdFromUrl}`;
        setTabs(prevTabs => {
          // If first tab doesn't have a session or has a different session, update it
          if (prevTabs.length === 0 || !prevTabs[0].sessionId || String(prevTabs[0].sessionId) !== String(sessionIdFromUrl)) {
            return sortTabsWithConfigLast([{ id: 1, sessionId: sessionIdFromUrl, label: sessionName }, ...prevTabs.slice(1)]);
          }
          return prevTabs;
        });
      }
    } else {
      // If no sessionId in URL, ensure first tab is empty (shows SessionSelector)
      setTabs(prevTabs => {
        if (prevTabs.length > 0 && prevTabs[0].id === 1 && prevTabs[0].sessionId) {
          return sortTabsWithConfigLast([{ id: 1, sessionId: null, label: "Tab 1" }, ...prevTabs.slice(1)]);
        }
        return prevTabs;
      });
    }
  }, [sessionIdFromUrl, transcriptions]);

  // Ensure sessions are loaded (important for basic login / direct entry)
  useEffect(() => {
    if (!Array.isArray(transcriptions) || transcriptions.length === 0) {
      dispatch(getAllTranscriptionsAsync());
    }
  }, [dispatch, transcriptions]);

  const handleAddTab = () => {
    if (tabs.length >= 5) return; // Max 5 tabs
    const newTab = {
      id: nextTabId,
      sessionId: null,
      label: `Tab ${nextTabId}`
    };
    setTabs(sortTabsWithConfigLast([...tabs, newTab]));
    setActiveTab(String(nextTabId));
    setNextTabId(nextTabId + 1);
  };

  const handleRemoveTab = useCallback((tabId) => {
    setTabs(prevTabs => {
      // Don't allow removing the Settings/Config tab
      const tabToRemove = prevTabs.find(tab => tab.id === tabId);
      if (tabToRemove?.isConfig) return prevTabs;
      
      // Clear persisted selections for this session when tab is closed
      if (tabToRemove?.sessionId) {
        const sessionIdStr = String(tabToRemove.sessionId);
        
        // Clear from memory stores
        if (window.selectedItemsStore) {
          window.selectedItemsStore.delete(sessionIdStr);
        }
        if (window.selectedSpeakersStore) {
          window.selectedSpeakersStore.delete(sessionIdStr);
        }
        if (window.sessionDataStore) {
          window.sessionDataStore.delete(sessionIdStr);
        }
        if (window.speakerSectionAssignmentsStore) {
          delete window.speakerSectionAssignmentsStore[sessionIdStr];
        }
        
        // Clear from localStorage
        try {
          // Clear selectedItemsStore
          const parsed = loadSelectedItemsForCurrentUser();
          if (Object.keys(parsed).length > 0) {
            delete parsed[sessionIdStr];
            saveSelectedItemsForCurrentUser(parsed);
          }
          
          // Clear selectedSpeakersStore
          const storedSpeakers = localStorage.getItem('selectedSpeakersStore');
          if (storedSpeakers) {
            const parsed = JSON.parse(storedSpeakers);
            delete parsed[sessionIdStr];
            localStorage.setItem('selectedSpeakersStore', JSON.stringify(parsed));
          }
          
        } catch (e) {
          console.error('[Multiscreen] Failed to clear selections from localStorage:', e);
        }
      }
      
      // Filter out the tab to remove
      const newTabs = prevTabs.filter(tab => tab.id !== tabId);
      
      // Check if there are any session tabs left (non-config tabs)
      const sessionTabs = newTabs.filter(tab => !tab.isConfig);
      
      // If no session tabs left, create an empty tab for session selection
      if (sessionTabs.length === 0) {
        const emptyTab = { id: 1, sessionId: null, label: "Tab 1" };
        const sortedTabs = sortTabsWithConfigLast([emptyTab, ...newTabs.filter(tab => tab.isConfig)]);
        
        // Switch to the empty tab
        if (String(tabId) === activeTab) {
          setActiveTab("1");
        }
        
        return sortedTabs;
      }
      
      // If removing the active tab, switch to the first available tab
      if (String(tabId) === activeTab) {
        const firstTab = newTabs.find(tab => !tab.isConfig) || newTabs[0];
        setActiveTab(String(firstTab?.id || "1"));
      }
      
      return sortTabsWithConfigLast(newTabs);
    });
  }, [activeTab]);

  const handleTabChange = useCallback((key) => {
    setTabs(prevTabs => {
      // Before switching, save state of current tab if it's a session
      const currentTab = prevTabs.find(tab => String(tab.id) === activeTab);
      if (currentTab && currentTab.sessionId && !currentTab.isConfig) {
        // Trigger save event for the current session
        window.dispatchEvent(new CustomEvent('save-session-state', {
          detail: { sessionId: currentTab.sessionId }
        }));
      }
      return prevTabs.map(t => ({
        ...t,
        isActive: String(t.id) === key
      }));
    });
    setActiveTab(key);
  }, [activeTab]);

  const handleSessionIdChange = (tabId, sessionId) => {
    const session = (transcriptions || []).find(s => String(s.id) === String(sessionId));
    const sessionName = session?.name || `Session ${sessionId}`;
    
    setTabs(sortTabsWithConfigLast(tabs.map(tab => 
      tab.id === tabId ? { ...tab, sessionId, label: sessionName } : tab
    )));
  };

  const handleAddSessionFromSelector = (sessionId) => {
    if (tabs.length >= 5) return; // Max 5 tabs
    const session = (transcriptions || []).find(s => String(s.id) === String(sessionId));
    const sessionName = session?.name || `Session ${sessionId}`;
    
    const newTab = {
      id: nextTabId,
      sessionId: String(sessionId),
      label: sessionName
    };
    setTabs(sortTabsWithConfigLast([...tabs, newTab]));
    setActiveTab(String(nextTabId));
    setNextTabId(nextTabId + 1);
    onSelectorClose();
  };

  // Expose functions to global scope for SidebarLayout to use
  useEffect(() => {
    if (window.multiscreenTabsStore) {
      window.multiscreenTabsStore.tabs = tabs;
      window.multiscreenTabsStore.activeTab = activeTab;
      window.multiscreenTabsStore.nextTabId = nextTabId;
      window.multiscreenTabsStore.storeVersion = storeVersion;
      window.multiscreenTabsStore.handleTabChange = handleTabChange;
      window.multiscreenTabsStore.handleRemoveTab = handleRemoveTab;
      window.multiscreenTabsStore.handleAddSessionFromSelector = handleAddSessionFromSelector;
      window.multiscreenTabsStore.onSelectorOpen = onSelectorOpen;
    }
  }, [tabs, activeTab, nextTabId, storeVersion, handleTabChange, handleRemoveTab, handleAddSessionFromSelector, onSelectorOpen]);

  useEffect(() => {
    if (!window.sessionDataStore) return;
    const activeSessionIds = new Set(
      (tabs || [])
        .filter((tab) => tab?.sessionId)
        .map((tab) => String(tab.sessionId))
    );
    window.sessionDataStore.forEach((_value, key) => {
      if (!activeSessionIds.has(String(key))) {
        window.sessionDataStore.delete(key);
      }
    });
  }, [tabs]);

  return (
    <div className="h-full w-full bg-surfaceContainer flex flex-col overflow-hidden -mt-2">
      {/* Content area - only render active tab to prevent multiple audio players */}
      <div className="flex-1 overflow-hidden relative">
        {(() => {
          const activeTabData = tabs.find(tab => String(tab.id) === activeTab);
          if (!activeTabData) return null;
          
          return (
            <div className="h-full w-full">
              {activeTabData.isConfig ? (
                <SitzungViewConfig
                  key={`config-${activeTabData.id}-${activeTabData.sessionId}`}
                  sessionId={activeTabData.sessionId}
                  localNavData={activeTabData.configData?.localNavData || []}
                  speakers={activeTabData.configData?.speakers || []}
                  paragraphChildren={activeTabData.configData?.paragraphChildren || []}
                  openTabs={tabs.filter(t => !t.isConfig && t.sessionId)}
                />
              ) : activeTabData.sessionId ? (
                <SessionWrapper 
                  key={`session-wrapper-${activeTabData.id}-${activeTabData.sessionId}`}
                  sessionId={activeTabData.sessionId}
                  isActive={true}
                />
              ) : (
                <SessionSelector
                  key={`selector-${activeTabData.id}`}
                  onSelect={(sessionId) => handleSessionIdChange(activeTabData.id, sessionId)}
                />
              )}
            </div>
          );
        })()}
      </div>
      
      {/* Session Selector Modal */}
      <Modal isOpen={isSelectorOpen} onClose={onSelectorClose} size="4xl" scrollBehavior="inside">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Select Translation</ModalHeader>
              <ModalBody>
                <SessionSelectorModal
                  onSelect={handleAddSessionFromSelector}
                  onClose={onClose}
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

// Wrapper component that renders Session with the sessionId from URL params
const SessionWrapper = ({ sessionId, isActive }) => {
  return (
    <div className="h-full w-full overflow-hidden px-4">
      <Session sessionIdOverride={sessionId} isEmbedded={true} isActive={isActive} />
    </div>
  );
};

const SessionSelector = ({ onSelect }) => {
  const dispatch = useDispatch();
  const { transcriptions } = useSelector((state) => state.transcriptions);
  const { tags } = useSelector((state) => state.tags);
  const { relations } = useSelector((state) => state.transcriptionTags);
  
  // Filter states
  const [searchText, setSearchText] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedTagFilters, setSelectedTagFilters] = useState(new Set());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  const transcriptionsList = Array.isArray(transcriptions) ? transcriptions : [];

  // Load tags on mount
  useEffect(() => {
    dispatch(getAllTagsAsync());
    dispatch(getAllTranscriptionTagsAsync());
  }, [dispatch]);

  // Status name mapping
  const statusName = {
    "completed": "Fertig",
    "in progress": "In Bearbeitung",
    "correcting with gpt": "Nachbearbeitung",
    "transcription running":"Transkription"
  };

  // Available statuses
  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    transcriptionsList.forEach(session => {
      if (session.stage) {
        statuses.add(session.stage);
      }
    });
    return Array.from(statuses).sort();
  }, [transcriptionsList]);

  // Tags by ID
  const tagsById = useMemo(() => {
    const map = new Map();
    (tags || []).forEach((tag) => {
      map.set(tag.id, tag);
    });
    return map;
  }, [tags]);

  // Transcription-tag mapping
  const transcriptionTagMap = useMemo(() => {
    const map = new Map();
    (relations || []).forEach((relation) => {
      const tag = relation.tag || tagsById.get(relation.tag_id);
      if (!tag) return;
      if (!map.has(relation.transcription_id)) {
        map.set(relation.transcription_id, []);
      }
      map.get(relation.transcription_id).push(tag);
    });
    return map;
  }, [relations, tagsById]);

  // Filtered transcriptions
  const filteredTranscriptions = useMemo(() => {
    let filtered = [...transcriptionsList];

    // Show all sessions (no stage filter)

    // Text search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(session => 
        session.name?.toLowerCase().includes(searchLower) ||
        session.description?.toLowerCase().includes(searchLower) ||
        session.created_by_microsoft_graph_username?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (selectedStatuses.size > 0) {
      filtered = filtered.filter(session => selectedStatuses.has(session.stage));
    }

    // Date range filter
    if (fromDate || toDate) {
      filtered = filtered.filter(session => {
        if (!session.start) return false;
        
        const sessionDate = new Date(session.start);
        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : new Date();
        
        if (from && sessionDate < from) return false;
        if (to && sessionDate > to) return false;
        
        return true;
      });
    }

    // Tag filter
    if (selectedTagFilters.size > 0) {
      const requiredTagIds = new Set(
        Array.from(selectedTagFilters).map((key) => parseInt(key, 10))
      );
      filtered = filtered.filter((session) => {
        const sessionTags = transcriptionTagMap.get(session.id) || [];
        if (sessionTags.length === 0) return false;
        const sessionTagIds = sessionTags.map((tag) => tag.id);
        return Array.from(requiredTagIds).every((tagId) =>
          sessionTagIds.includes(tagId)
        );
      });
    }

    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [
    transcriptionsList,
    searchText,
    selectedStatuses,
    selectedTagFilters,
    fromDate,
    toDate,
    transcriptionTagMap
  ]);

  const handleSelect = (sessionId) => {
    if (sessionId) {
      onSelect(sessionId);
    }
  };

  return (
    <div className="flex items-center justify-center h-full overflow-auto p-6">
      <div className="w-full max-w-6xl">
        <p className="text-onSurface/60 mb-4 text-center">Select a session to view</p>
        
        {/* Filter Section */}
        <div className="mb-4 border-b border-gray-200 pb-4 bg-surface rounded-xl p-4">
          <div className="flex flex-nowrap items-center gap-3 overflow-x-auto">
            {/* Free Text Search */}
            <div className="flex-1 min-w-[180px]">
              <Input
                placeholder="Sitzungen suchen..."
                value={searchText}
                onValueChange={setSearchText}
                startContent={<Search className="w-4 h-4 text-gray-400" />}
                variant="bordered"
                size="sm"
              />
            </div>

            {/* Date Range */}
            <Input
              type="date"
              label="Von"
              value={fromDate}
              onValueChange={setFromDate}
              variant="bordered"
              size="sm"
              className="w-40 flex-shrink-0"
            />

            <Input
              type="date"
              label="bis"
              value={toDate}
              onValueChange={setToDate}
              variant="bordered"
              size="sm"
              className="w-40 flex-shrink-0"
            />

            {/* Status Selector */}
            <Select
              selectedKeys={selectedStatuses}
              onSelectionChange={setSelectedStatuses}
              variant="bordered"
              size="sm"
              className="w-32 flex-shrink-0"
              selectionMode="multiple"
              placeholder="Status"
              aria-label="Filter by status"
            >
              {availableStatuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {statusName[status.toLowerCase()]}
                </SelectItem>
              ))}
            </Select>

            {/* Tag Selector */}
            <Select
              selectedKeys={selectedTagFilters}
              onSelectionChange={setSelectedTagFilters}
              variant="bordered"
              size="sm"
              className="w-32 flex-shrink-0"
              selectionMode="multiple"
              placeholder="Tags"
              aria-label="Filter by tags"
            >
              {(tags || []).map((tag) => (
                <SelectItem key={tag.id.toString()} value={tag.id.toString()}>
                  {tag.name}
                </SelectItem>
              ))}
            </Select>

            {/* Clear All Button */}
            {(searchText || selectedStatuses.size > 0 || selectedTagFilters.size > 0 || fromDate || toDate) && (
              <Button
                size="sm"
                variant="light"
                color="danger"
                startContent={<X className="w-4 h-4" />}
                className="flex-shrink-0"
                onPress={() => {
                  setSearchText("");
                  setSelectedStatuses(new Set());
                  setSelectedTagFilters(new Set());
                  setFromDate("");
                  setToDate("");
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Transcriptions List */}
        <div className="max-h-[60vh] overflow-auto bg-surface rounded-xl">
          <ul className="divide-y divide-gray-200 p-0">
            {filteredTranscriptions.map((session) => {
              const sessionTags = transcriptionTagMap.get(session.id) || [];

              return (
                <li
                  key={session.id}
                  className="cursor-pointer hover:bg-secondaryContainer hover:rounded-xl transition-shadow"
                  onClick={() => handleSelect(session.id)}
                >
                  <div className="px-4 py-3">
                    <div className="flex justify-between items-start gap-3">
                      {/* Left: Session Info */}
                      <div className="flex flex-col gap-1">
                        <div className="text-base font-semibold text-gray-900 flex items-center gap-2">
                          {session.name}
                          <Chip variant="bordered" 
                                className={clsx(
                                  'text-xs px-2 py-0.5 bg-gray-100 border-0',        
                                      )}>
                            {statusName[session.stage?.toLowerCase()] || "In Bearbeitung"}
                          </Chip>
                        </div>
                        <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
                          <span>{session.start.split("T")[0]}</span>
                          <span>{session.start.split("T")[1].split("Z")[0]}</span>
                          {sessionTags.length > 0 && (
                            <>
                              <strong>Tags:</strong>
                              {sessionTags.map((tag) => (
                                <Chip
                                  key={`${session.id}-tag-${tag.id}`}
                                  variant="bordered"
                                  className={clsx('text-xs px-2 py-0.5 bg-blue-100 border-0')}
                                >
                                  {tag.name}
                                </Chip>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {filteredTranscriptions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Keine Sitzungen gefunden
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SessionSelectorModal = ({ onSelect, onClose }) => {
  const dispatch = useDispatch();
  const { transcriptions } = useSelector((state) => state.transcriptions);
  const { tags } = useSelector((state) => state.tags);
  const { relations } = useSelector((state) => state.transcriptionTags);
  
  // Filter states
  const [searchText, setSearchText] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedTagFilters, setSelectedTagFilters] = useState(new Set());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  const transcriptionsList = Array.isArray(transcriptions) ? transcriptions : [];

  // Load tags on mount
  useEffect(() => {
    dispatch(getAllTagsAsync());
    dispatch(getAllTranscriptionTagsAsync());
  }, [dispatch]);

  // Status name mapping
  const statusName = {
    "completed": "Fertig",
    "in progress": "In Bearbeitung",
    "correcting with gpt": "Nachbearbeitung",
    "transcription running":"Transkription"
  };

  // Available statuses
  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    transcriptionsList.forEach(session => {
      if (session.stage) {
        statuses.add(session.stage);
      }
    });
    return Array.from(statuses).sort();
  }, [transcriptionsList]);

  // Tags by ID
  const tagsById = useMemo(() => {
    const map = new Map();
    (tags || []).forEach((tag) => {
      map.set(tag.id, tag);
    });
    return map;
  }, [tags]);

  // Transcription-tag mapping
  const transcriptionTagMap = useMemo(() => {
    const map = new Map();
    (relations || []).forEach((relation) => {
      const tag = relation.tag || tagsById.get(relation.tag_id);
      if (!tag) return;
      if (!map.has(relation.transcription_id)) {
        map.set(relation.transcription_id, []);
      }
      map.get(relation.transcription_id).push(tag);
    });
    return map;
  }, [relations, tagsById]);

  // Filtered transcriptions
  const filteredTranscriptions = useMemo(() => {
    let filtered = [...transcriptionsList];

    // Show all sessions (no stage filter)

    // Text search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(session => 
        session.name?.toLowerCase().includes(searchLower) ||
        session.description?.toLowerCase().includes(searchLower) ||
        session.created_by_microsoft_graph_username?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (selectedStatuses.size > 0) {
      filtered = filtered.filter(session => selectedStatuses.has(session.stage));
    }

    // Date range filter
    if (fromDate || toDate) {
      filtered = filtered.filter(session => {
        if (!session.start) return false;
        
        const sessionDate = new Date(session.start);
        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : new Date();
        
        if (from && sessionDate < from) return false;
        if (to && sessionDate > to) return false;
        
        return true;
      });
    }

    // Tag filter
    if (selectedTagFilters.size > 0) {
      const requiredTagIds = new Set(
        Array.from(selectedTagFilters).map((key) => parseInt(key, 10))
      );
      filtered = filtered.filter((session) => {
        const sessionTags = transcriptionTagMap.get(session.id) || [];
        if (sessionTags.length === 0) return false;
        const sessionTagIds = sessionTags.map((tag) => tag.id);
        return Array.from(requiredTagIds).every((tagId) =>
          sessionTagIds.includes(tagId)
        );
      });
    }

    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [
    transcriptionsList,
    searchText,
    selectedStatuses,
    selectedTagFilters,
    fromDate,
    toDate,
    transcriptionTagMap
  ]);

  const handleSelect = (sessionId) => {
    if (sessionId) {
      onSelect(sessionId);
    }
  };

  return (
    <div className="w-full">
      {/* Filter Section */}
      <div className="mb-4 border-b border-gray-200 pb-4">
        <div className="flex flex-nowrap items-center gap-3 overflow-x-auto">
          {/* Free Text Search */}
          <div className="flex-1 min-w-[180px]">
            <Input
              placeholder="Sitzungen suchen..."
              value={searchText}
              onValueChange={setSearchText}
              startContent={<Search className="w-4 h-4 text-gray-400" />}
              variant="bordered"
              size="sm"
            />
          </div>

          {/* Date Range */}
          <Input
            type="date"
            label="Von"
            value={fromDate}
            onValueChange={setFromDate}
            variant="bordered"
            size="sm"
            className="w-40 flex-shrink-0"
          />

          <Input
            type="date"
            label="bis"
            value={toDate}
            onValueChange={setToDate}
            variant="bordered"
            size="sm"
            className="w-40 flex-shrink-0"
          />

          {/* Status Selector */}
          <Select
            selectedKeys={selectedStatuses}
            onSelectionChange={setSelectedStatuses}
            variant="bordered"
            size="sm"
            className="w-32 flex-shrink-0"
            selectionMode="multiple"
            placeholder="Status"
            aria-label="Filter by status"
          >
            {availableStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {statusName[status.toLowerCase()]}
              </SelectItem>
            ))}
          </Select>

          {/* Tag Selector */}
          <Select
            selectedKeys={selectedTagFilters}
            onSelectionChange={setSelectedTagFilters}
            variant="bordered"
            size="sm"
            className="w-32 flex-shrink-0"
            selectionMode="multiple"
            placeholder="Tags"
            aria-label="Filter by tags"
          >
            {(tags || []).map((tag) => (
              <SelectItem key={tag.id.toString()} value={tag.id.toString()}>
                {tag.name}
              </SelectItem>
            ))}
          </Select>

          {/* Clear All Button */}
          {(searchText || selectedStatuses.size > 0 || selectedTagFilters.size > 0 || fromDate || toDate) && (
            <Button
              size="sm"
              variant="light"
              color="danger"
              startContent={<X className="w-4 h-4" />}
              className="flex-shrink-0"
              onPress={() => {
                setSearchText("");
                setSelectedStatuses(new Set());
                setSelectedTagFilters(new Set());
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Transcriptions List */}
      <div className="max-h-[50vh] overflow-auto">
        <ul className="divide-y divide-gray-200 p-0">
          {filteredTranscriptions.map((session) => {
            const sessionTags = transcriptionTagMap.get(session.id) || [];

            return (
              <li
                key={session.id}
                className="cursor-pointer hover:bg-secondaryContainer hover:rounded-xl transition-shadow"
                onClick={() => handleSelect(session.id)}
              >
                <div className="px-4 py-3">
                  <div className="flex justify-between items-start gap-3">
                    {/* Left: Session Info */}
                    <div className="flex flex-col gap-1">
                      <div className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        {session.name}
                        <Chip variant="bordered" 
                              className={clsx(
                                'text-xs px-2 py-0.5 bg-gray-100 border-0',        
                                    )}>
                          {statusName[session.stage?.toLowerCase()] || "In Bearbeitung"}
                        </Chip>
                      </div>
                      <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
                        <span>{session.start.split("T")[0]}</span>
                        <span>{session.start.split("T")[1].split("Z")[0]}</span>
                        {sessionTags.length > 0 && (
                          <>
                            <strong>Tags:</strong>
                            {sessionTags.map((tag) => (
                              <Chip
                                key={`${session.id}-tag-${tag.id}`}
                                variant="bordered"
                                className={clsx('text-xs px-2 py-0.5 bg-blue-100 border-0')}
                              >
                                {tag.name}
                              </Chip>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {filteredTranscriptions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Keine Sitzungen gefunden
          </div>
        )}
      </div>
    </div>
  );
};

export default Multiscreen;
